import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Ignore events from the Custom Variants, wait for them to trigger the main variant update
    if (event.target.matches('[data-name="color"], [data-name="gsm"], [data-name="sheet_size"]')) {
      return;
    }

    this.updateSelectedOption(event.target);

    const selectedElement = event.target instanceof HTMLSelectElement ? this.selectedOption : event.target;

    console.log('[VariantPicker] variantChanged triggered by', event.target);
    console.log('[VariantPicker] selectedElement resolved to', selectedElement, selectedElement?.dataset);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedElement.dataset.optionValueId ?? '',
      })
    );

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedElement.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const reqUrl = this.buildRequestUrl(selectedElement);
    console.log('[VariantPicker] buildRequestUrl ->', reqUrl);
    this.fetchUpdatedSection(reqUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (selectedElement.dataset.variantId) {
        url.searchParams.set('variant', selectedElement.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const el = this.querySelector(
        `[data-option-value-id="${target}"]`
      );
      if (!el) throw new Error('Target not found');
      target = el;
    }

    let optionEl = target;
    if (target instanceof HTMLSelectElement) {
      optionEl = target.options[target.selectedIndex];
    }

    const variantId =
      optionEl?.dataset.variantId ??
      this.selectedOption?.dataset.variantId ??
      null;

    const material =
      optionEl?.dataset.material ??
      this.selectedOption?.dataset.material ??
      null;

    const prm =
      optionEl?.dataset.prmValue ??
      this.selectedOption?.dataset.prmValue ??
      null;

    this.priceUpdate(variantId);
    this.stockupdate(material, prm);



    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const option = Array.from(target.options).find(
        (o) => o.value === target.value
      );
      if (!option) throw new Error('Option not found');

      for (const o of target.options) {
        o.removeAttribute('selected');
      }
      option.setAttribute('selected', 'selected');
    }
  }

  priceUpdate(variantId) {
    if (!variantId) return;
    // console.log('Price update:', variantId);
  }

  stockupdate(material, prm) {
    if (!material) return;

    this.dispatchEvent(
      new CustomEvent('stock:update', {
        detail: { material, prm },
        bubbles: true,
      })
    );
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      params.push(
        `option_values=${
          this.selectedOptionsValues.length
            ? sourceSelectedOptionsValues.join(',')
            : selectedOption.dataset.optionValueId
        }`
      );
    }

    if (selectedOption && selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    }

    if (
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component')
    ) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((res) => res.text())
      .then((htmlText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        console.log('[VariantPicker] Fetched HTML:', html);
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const json = html.querySelector(
          'variant-picker script[type="application/json"]'
        )?.textContent;

        if (!json) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Safely get selectedOptionId - the getter throws if no option[selected] is found
          // This can happen on PDP when option[selected] attr isn't set after morph
          let selectedOptionId;
          try {
            selectedOptionId = this.selectedOptionId;
          } catch (e) {
            // Fallback: find selected option from the fetched HTML source directly
            const sourceVariantPicker = html.querySelector(this.tagName.toLowerCase());
            const sourceSelectedOption = sourceVariantPicker?.querySelector(
              'select option[selected], fieldset input:checked'
            );
            selectedOptionId = sourceSelectedOption?.dataset.optionValueId;
            console.log('[VariantPicker] selectedOptionId fallback from source HTML:', selectedOptionId);
          }

          if (selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(json),
                selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                }
              )
            );
          }
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err);
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;
    const source = newHtml.querySelector(this.tagName.toLowerCase());
    if (!source) throw new Error('Variant picker not found');

    if (source instanceof HTMLElement) {
      const { productId, productUrl } = source.dataset;

      if (productId && productUrl && this.dataset.productId !== productId) {
        newProduct = { id: productId, url: productUrl };
      }

      this.dataset.productId = productId;
      this.dataset.productUrl = productUrl;
    }

    morph(this, source);
    
    // Force DOM select boxes to reflect the selected option after morph
    const selects = this.querySelectorAll('select');
    selects.forEach(select => {
      const selectedOption = select.querySelector('option[selected]');
      if (selectedOption && select.value !== selectedOption.value) {
        select.value = selectedOption.value;
      }
    });

    return newProduct;
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('Main not found');
    morph(main, newMain);
  }

  get selectedOption() {
    const el = this.querySelector(
      'select option[selected], fieldset input:checked'
    );
    return el instanceof HTMLElement ? el : undefined;
  }

  get selectedOptionId() {
    const id = this.selectedOption?.dataset.optionValueId;
    if (!id) throw new Error('No option value ID');
    return id;
  }

  get selectedOptionsValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    ).map((el) => {
      const id = el.dataset.optionValueId;
      if (!id) throw new Error('No option value ID');
      return id;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}












// import { Component } from '@theme/component';
// import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
// import { morph } from '@theme/morph';

// /**
//  * A custom element that manages a variant picker.
//  *
//  * @template {import('@theme/component').Refs} [Refs = {}]
//  *
//  * @extends Component<Refs>
//  */
// export default class VariantPicker extends Component {
//   /** @type {string | undefined} */
//   #pendingRequestUrl;

//   /** @type {AbortController | undefined} */
//   #abortController;

//   connectedCallback() {
//     super.connectedCallback();

//     this.addEventListener('change', this.variantChanged.bind(this));
//   }

//   /**
//    * Handles the variant change event.
//    * @param {Event} event - The variant change event.
//    */
//   variantChanged(event) {
//     if (!(event.target instanceof HTMLElement)) return;

//     this.updateSelectedOption(event.target);
//     this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

//     const isOnProductPage =
//       Theme.template.name === 'product' &&
//       !event.target.closest('product-card') &&
//       !event.target.closest('quick-add-dialog');

//     // Morph the entire main content for combined listings child products, because changing the product
//     // might also change other sections depending on recommendations, metafields, etc.
//     const currentUrl = this.dataset.productUrl?.split('?')[0];
//     const newUrl = event.target.dataset.connectedProductUrl;
//     const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

//     this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

//     const url = new URL(window.location.href);

//     if (isOnProductPage) {
//       if (event.target.dataset.variantId) {
//         url.searchParams.set('variant', event.target.dataset.variantId);
//       } else {
//         url.searchParams.delete('variant');
//       }
//     }

//     // Change the path if the option is connected to another product via combined listing.
//     if (loadsNewProduct) {
//       url.pathname = newUrl;
//     }

//     if (url.href !== window.location.href) {
//       history.replaceState({}, '', url.toString());
//     }
//   }

//   /**
//    * Updates the selected option.
//    * @param {string | Element} target - The target element.
//    */

//   updateSelectedOption(target) {
//   if (typeof target === 'string') {
//     const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
//     if (!targetElement) throw new Error('Target element not found');
//     target = targetElement;
//   }

//   const variantId =
//     target.dataset.variantId ||
//     this.selectedOption?.dataset.variantId ||
//     null;

  
//   priceUpdate(variantId);

//   if (target instanceof HTMLInputElement) {
//     target.checked = true;
//   }

//   if (target instanceof HTMLSelectElement) {
//     const newValue = target.value;
//     const newSelectedOption = Array.from(target.options).find(
//       (option) => option.value === newValue
//     );
//     if (!newSelectedOption) throw new Error('Option not found');

//     for (const option of target.options) {
//       option.removeAttribute('selected');
//     }
//     newSelectedOption.setAttribute('selected', 'selected');
//   }
// }


//   /**
//    * Builds the request URL.
//    * @param {HTMLElement} selectedOption - The selected option.
//    * @param {string | null} [source] - The source.
//    * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
//    * @returns {string} The request URL.
//    */
//   buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
//     // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
//     // We create a new URL and abort the previous fetch request if it's still pending.
//     let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
//     this.#pendingRequestUrl = productUrl;
//     const params = [];

//     if (this.selectedOptionsValues.length && !source) {
//       params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
//     } else if (source === 'product-card') {
//       if (this.selectedOptionsValues.length) {
//         params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
//       } else {
//         params.push(`option_values=${selectedOption.dataset.optionValueId}`);
//       }
//     }

//     // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
//     if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
//       if (productUrl?.includes('?')) {
//         productUrl = productUrl.split('?')[0];
//       }
//       return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
//     }
//     return `${productUrl}?${params.join('&')}`;
//   }

//   /**
//    * Fetches the updated section.
//    * @param {string} requestUrl - The request URL.
//    * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
//    */
//   fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
//     // We use this to abort the previous fetch request if it's still pending.
//     this.#abortController?.abort();
//     this.#abortController = new AbortController();

//     fetch(requestUrl, { signal: this.#abortController.signal })
//       .then((response) => response.text())
//       .then((responseText) => {
//         this.#pendingRequestUrl = undefined;
//         const html = new DOMParser().parseFromString(responseText, 'text/html');
//         // Defer is only useful for the initial rendering of the page. Remove it here.
//         html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

//         const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
//         if (!textContent) return;

//         if (shouldMorphMain) {
//           this.updateMain(html);
//         } else {
//           const newProduct = this.updateVariantPicker(html);

//           // We grab the variant object from the response and dispatch an event with it.
//           if (this.selectedOptionId) {
//             this.dispatchEvent(
//               new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
//                 html,
//                 productId: this.dataset.productId ?? '',
//                 newProduct,
//               })
//             );
//           }
//         }
//       })
//       .catch((error) => {
//         if (error.name === 'AbortError') {
//           console.log('Fetch aborted by user');
//         } else {
//           console.error(error);
//         }
//       });
//   }

//   /**
//    * @typedef {Object} NewProduct
//    * @property {string} id
//    * @property {string} url
//    */

//   /**
//    * Re-renders the variant picker.
//    * @param {Document} newHtml - The new HTML.
//    * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
//    */
//   updateVariantPicker(newHtml) {
//     /** @type {NewProduct | undefined} */
//     let newProduct;

//     const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

//     if (!newVariantPickerSource) {
//       throw new Error('No new variant picker source found');
//     }

//     // For combined listings, the product might have changed, so update the related data attribute.
//     if (newVariantPickerSource instanceof HTMLElement) {
//       const newProductId = newVariantPickerSource.dataset.productId;
//       const newProductUrl = newVariantPickerSource.dataset.productUrl;

//       if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
//         newProduct = { id: newProductId, url: newProductUrl };
//       }

//       this.dataset.productId = newProductId;
//       this.dataset.productUrl = newProductUrl;
//     }

//     morph(this, newVariantPickerSource);

//     return newProduct;
//   }

//   /**
//    * Re-renders the entire main content.
//    * @param {Document} newHtml - The new HTML.
//    */
//   updateMain(newHtml) {
//     const main = document.querySelector('main');
//     const newMain = newHtml.querySelector('main');

//     if (!main || !newMain) {
//       throw new Error('No new main source found');
//     }

//     morph(main, newMain);
//   }

//   /**
//    * Gets the selected option.
//    * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
//    */
//   get selectedOption() {
//     const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

//     if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
//       return undefined;
//     }

//     return selectedOption;
//   }

//   /**
//    * Gets the selected option ID.
//    * @returns {string | undefined} The selected option ID.
//    */
//   get selectedOptionId() {
//     const { selectedOption } = this;
//     if (!selectedOption) return undefined;
//     const { optionValueId } = selectedOption.dataset;

//     if (!optionValueId) {
//       throw new Error('No option value ID found');
//     }

//     return optionValueId;
//   }

//   /**
//    * Gets the selected options values.
//    * @returns {string[]} The selected options values.
//    */
//   get selectedOptionsValues() {
//     /** @type HTMLElement[] */
//     const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

//     return selectedOptions.map((option) => {
//       const { optionValueId } = option.dataset;

//       if (!optionValueId) throw new Error('No option value ID found');

//       return optionValueId;
//     });
//   }
// }

// if (!customElements.get('variant-picker')) {
//   customElements.define('variant-picker', VariantPicker);
// }